import { useCallback, useEffect, useMemo, useRef } from 'react'
import { MessageSquare, X } from 'lucide-react'
import type { Editor } from '@tiptap/core'
import type { CommentDocument } from '@/lib/db/rxdb/hooks/entities/comments'
import { useComments } from '@/lib/db/rxdb/hooks'
import { CommentItem } from '@/components/features/comments/CommentItem'
import { CommentEditor } from '@/components/features/comments/CommentEditor'

interface CommentSidebarProps {
  pageId: string
  workspaceId: string
  editor: Editor | null
  activeAnchorId: string | null
  onClose?: () => void
  onCancelNewComment?: () => void
}

interface CommentThread {
  anchorId: string
  root: CommentDocument
  replies: Array<CommentDocument>
  isResolved: boolean
}

export function CommentSidebar({
  pageId,
  workspaceId,
  editor,
  activeAnchorId,
  onClose,
  onCancelNewComment,
}: CommentSidebarProps) {
  const { comments } = useComments({
    entityType: 'page',
    entityId: pageId,
  })

  const threads = useMemo<Array<CommentThread>>(() => {
    if (!comments) return []
    const active = comments.filter((c) => !c.isDeleted && c.anchorId)
    const grouped = new Map<string, Array<CommentDocument>>()
    for (const c of active) {
      const key = c.anchorId as string
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key)!.push(c)
    }

    const result: Array<CommentThread> = []
    for (const [anchorId, list] of grouped) {
      const sorted = [...list].sort((a, b) => {
        if (!a.parentCommentId && b.parentCommentId) return -1
        if (a.parentCommentId && !b.parentCommentId) return 1
        return a.createdOn - b.createdOn
      })
      const root = sorted.find((c) => !c.parentCommentId)
      if (!root) continue
      const replies = sorted.filter((c) => c.parentCommentId === root.id)
      result.push({
        anchorId,
        root,
        replies,
        isResolved: !!root.isResolved,
      })
    }

    return result.sort((a, b) => b.root.createdOn - a.root.createdOn)
  }, [comments])

  // Keep editor highlight in sync with thread resolution state.
  const lastResolvedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!editor) return
    const next = new Set<string>()
    for (const t of threads) {
      if (t.isResolved) {
        next.add(t.anchorId)
        if (!lastResolvedRef.current.has(t.anchorId)) {
          editor.commands.resolveCommentHighlight(t.anchorId)
        }
      }
    }
    lastResolvedRef.current = next
  }, [threads, editor])

  const handleClickThread = useCallback(
    (anchorId: string) => {
      if (!editor) return
      const { doc } = editor.state
      doc.descendants((node, pos) => {
        for (const mark of node.marks) {
          if (
            mark.type.name === 'commentHighlight' &&
            mark.attrs.commentId === anchorId
          ) {
            editor.commands.setTextSelection(pos)
            editor.commands.scrollIntoView()
            return false
          }
        }
      })
    },
    [editor],
  )

  const showNewEditor =
    !!activeAnchorId && !threads.some((t) => t.anchorId === activeAnchorId)

  return (
    <div className="sticky top-0 flex h-screen w-80 shrink-0 flex-col self-start border-l border-border bg-background">
      <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <MessageSquare className="h-4 w-4" />
          Comments
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close comments"
            title="Close comments"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {showNewEditor ? (
          <div className="rounded-md border border-border bg-muted/40 p-2">
            <CommentEditor
              entityType="page"
              entityId={pageId}
              anchorId={activeAnchorId}
              placeholder="Add a comment..."
              onCancel={onCancelNewComment}
              onSuccess={() => {
                /* keep anchor active so the new thread renders in place */
              }}
            />
          </div>
        ) : null}

        {threads.map((thread) => (
          <div
            key={thread.anchorId}
            className={
              'rounded-md transition-colors ' +
              (thread.anchorId === activeAnchorId
                ? 'ring-1 ring-primary/30'
                : '')
            }
            onClick={() => handleClickThread(thread.anchorId)}
          >
            <CommentItem
              comment={thread.root}
              replies={thread.replies}
              entityType="page"
              entityId={pageId}
              workspaceId={workspaceId}
              anchorId={thread.anchorId}
              level={0}
            />
          </div>
        ))}

        {threads.length === 0 && !activeAnchorId ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Select text and click the comment button to add inline comments.
          </div>
        ) : null}
      </div>
    </div>
  )
}
